import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ExpandedState } from "@tanstack/react-table";
import { AccountWithTransactions } from "@common/dto/reports/get-account-transactions-report-response.dto";
import { getGLExpandedState, shouldAutoExpandGL } from "./glViewBuilder";

interface Options {
    data: AccountWithTransactions[];
    reportKey: string;
    isReportLoaded: boolean;
}

export function useGeneralLedgerExpandedState({
    data,
    reportKey,
    isReportLoaded,
}: Options) {
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const autoExpandedReportKeyRef = useRef<string | null>(null);
    const expandAllState = useMemo(() => getGLExpandedState(data), [data]);

    useEffect(() => {
        if (!isReportLoaded || autoExpandedReportKeyRef.current === reportKey) {
            return;
        }
        autoExpandedReportKeyRef.current = data.length > 0 ? reportKey : null;
        setExpanded(shouldAutoExpandGL(data) ? expandAllState : {});
    }, [data, expandAllState, isReportLoaded, reportKey]);

    const isCollapsed = Object.keys(expanded).length === 0;

    const toggleAll = useCallback(() => {
        setExpanded((currentExpandedState) =>
            Object.keys(currentExpandedState).length === 0
                ? expandAllState
                : {},
        );
    }, [expandAllState]);

    return { expanded, setExpanded, isCollapsed, toggleAll };
}
